"use client"

import type React from "react"
import { useState } from "react"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { toast } from "sonner"
import { Mail, Phone, MapPin, Clock } from "lucide-react"

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires")
      return
    }

    setIsSubmitting(true)

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000"
      const response = await fetch(`${apiUrl}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || `Nouveau contact depuis ${process.env.NEXT_PUBLIC_COMPANY_NAME || "Barakhel Music"}`,
          message: formData.message,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        toast.success("Message envoyé avec succès! Nous vous répondrons dans les 24h.")
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" })
      } else {
        toast.error(data.error || "Erreur lors de l'envoi du message")
      }
    } catch (error) {
      console.error("Erreur:", error)
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: process.env.NEXT_PUBLIC_COMPANY_EMAIL || "barakhelmusic@gmail.com",
      description: "Réponse dans les 24h",
    },
    {
      icon: Phone,
      title: "Téléphone",
      value: process.env.NEXT_PUBLIC_COMPANY_PHONE || "+226 61 74 22 74",
      description: "Lundi - Samedi, 8h-19h",
    },
    {
      icon: MapPin,
      title: "Adresse",
      value: process.env.NEXT_PUBLIC_COMPANY_ADDRESS || "Samandin, Ouagadougou, Burkina Faso",
      description: "Boutique principale",
    },
    {
      icon: Clock,
      title: "Horaires",
      value: "Lun-Sam: 8h-19h",
      description: "Dimanche fermé",
    },
  ]

  return (
    <main className="min-h-screen bg-white dark:bg-dark-background">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-20">
        <div className="container">
          <h1 className="text-5xl font-bold mb-6">Contactez {process.env.NEXT_PUBLIC_COMPANY_NAME || "Barakhel Music"}</h1>
          <p className="text-xl text-white/90">Besoin de conseils ou d'informations? Notre équipe est à votre écoute</p>
        </div>
      </section>

      <div className="container py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-8 text-foreground dark:text-dark-foreground">Nos Coordonnées</h2>
            </div>

            {contactInfo.map((info, idx) => {
              const Icon = info.icon
              return (
                <div
                  key={idx}
                  className="bg-white dark:bg-primary-dark/10 p-6 rounded-xl border border-background-secondary dark:border-primary/20 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 dark:bg-primary/20 p-3 rounded-lg">
                      <Icon className="text-primary flex-shrink-0" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground dark:text-dark-foreground mb-1">{info.title}</h3>
                      <p className="text-primary font-semibold mb-1">{info.value}</p>
                      <p className="text-sm text-foreground-secondary dark:text-dark-foreground-secondary">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Contact Form & Map */}
          <div className="lg:col-span-2 space-y-12">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground dark:text-dark-foreground">
                Envoyez-nous un Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                      Sujet
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="input-field"
                      placeholder="Demande d'information, conseil produit, devis, etc."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-field min-h-40"
                    placeholder="Votre message..."
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer un Message"}
                </button>
              </form>
            </div>

            {/* Location Map */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground dark:text-dark-foreground">Localisation</h2>
              <div className="space-y-4">
                <p className="text-foreground-secondary dark:text-dark-foreground-secondary leading-relaxed">
                  Notre boutique est située à Samandin, Ouagadougou, au Burkina Faso. Venez découvrir notre large gamme d'instruments de musique et de matériel de sonorisation ou contactez-nous via les coordonnées ci-dessus.
                </p>
                <div className="bg-background dark:bg-primary-dark/10 rounded-xl overflow-hidden border border-background-secondary dark:border-primary/20 h-96">
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps/embed?pb=!1m10!1m8!1m3!1d1518.004770412755!2d-1.5680806570075847!3d12.37678645501554!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sfr!2snl!4v1763382716272!5m2!1sfr!2snl"
                  />
                </div>
              </div>
            </div>

            {/* Location Description */}
            {/* <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-4 text-foreground dark:text-dark-foreground">
                À Propos de Notre Localisation
              </h3>
              <div className="space-y-4 text-foreground-secondary dark:text-dark-foreground-secondary">
                <p>
                  <strong className="text-foreground dark:text-dark-foreground">Quartier Premium:</strong> Notre bureau
                  est situé dans le 8ème arrondissement, l'un des quartiers les plus prestigieux de Paris.
                </p>
                <p>
                  <strong className="text-foreground dark:text-dark-foreground">Accès Facile:</strong> À proximité
                  immédiate des stations de métro et des lignes de bus principales, nous sommes facilement accessibles.
                </p>
                <p>
                  <strong className="text-foreground dark:text-dark-foreground">Environnement Moderne:</strong> Nos
                  locaux offrent un cadre de travail moderne et agréable pour nos équipes.
                </p>
                <p>
                  <strong className="text-foreground dark:text-dark-foreground">Stationnement:</strong> Nous mettons à
                  disposition des emplacements de stationnement pour nos clients et partenaires.
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  )
}
